import React, { useState } from 'react';
import { ChevronRight, ChevronDown, File, Folder, Plus, Trash2, Edit, Loader2 } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { usePermissions } from '@/hooks/usePermissions';

interface FileNode {
  name: string;
  type: 'file' | 'directory';
  path?: string;
  children?: FileNode[];
}

interface FileExplorerProps {
  tree: FileNode;
  onFileSelect: (path: string) => void;
  onFileCreate: (path: string) => void;
  onFileDelete: (path: string) => void;
  onFileRename: (oldPath: string, newPath: string) => void;
  selectedFile?: string;
}

export const FileExplorer: React.FC<FileExplorerProps> = ({
  tree,
  onFileSelect,
  onFileCreate,
  onFileDelete,
  onFileRename,
  selectedFile
}) => {
  const { canEditFiles } = usePermissions();
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['root']));
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; path: string } | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalPath, setModalPath] = useState('');
  const [newName, setNewName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const toggleExpand = (path: string) => {
    const newExpanded = new Set(expanded);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpanded(newExpanded);
  };

  const renderNode = (node: FileNode, level = 0): JSX.Element => {
    const isExpanded = expanded.has(node.path || node.name);
    const isSelected = node.path === selectedFile;
    const indent = level * 16;

    return (
      <div key={node.path || node.name}>
        <div
          className={`
            flex items-center px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition-colors
            ${isSelected ? 'bg-purple-100 dark:bg-purple-900/30' : ''}
          `}
          style={{ paddingLeft: `${indent + 8}px` }}
          onClick={() => {
            if (node.type === 'directory') {
              toggleExpand(node.path || node.name);
            } else if (node.path) {
              onFileSelect(node.path);
            }
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            // Only show context menu for users who can edit files
            if (canEditFiles) {
              setContextMenu({ x: e.clientX, y: e.clientY, path: node.path || '' });
            }
          }}
        >
          {node.type === 'directory' && (
            <span className="mr-1 text-gray-500 dark:text-gray-400">
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </span>
          )}
          <span className="mr-2 text-gray-600 dark:text-gray-400">
            {node.type === 'directory' ? (
              <Folder size={16} className={isExpanded ? 'text-purple-600 dark:text-purple-400' : ''} />
            ) : (
              <File size={16} />
            )}
          </span>
          <span className="text-sm text-gray-900 dark:text-gray-100">{node.name}</span>
        </div>
        
        {node.type === 'directory' && isExpanded && node.children && (
          <div>
            {node.children.map(child => renderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // Close context menu when clicking outside
  React.useEffect(() => {
    const handleClick = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu]);

  return (
    <div className="h-full overflow-y-auto bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800">
      <div className="p-3 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
        <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">Files</h3>
        {canEditFiles && (
          <button
            onClick={() => {
              setModalPath('');
              setNewName('');
              setShowCreateModal(true);
            }}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
            title="New File"
          >
            <Plus size={16} className="text-gray-600 dark:text-gray-400" />
          </button>
        )}
      </div>
      
      {tree.children && tree.children.map(child => renderNode(child))}
      
      {contextMenu && canEditFiles && (
        <div
          className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg py-1"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
            onClick={() => {
              setModalPath(contextMenu.path);
              setNewName('');
              setShowCreateModal(true);
              setContextMenu(null);
            }}
          >
            <Plus size={14} />
            New File
          </button>
          <button
            className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
            onClick={() => {
              const currentName = contextMenu.path.split('/').pop() || '';
              setModalPath(contextMenu.path);
              setNewName(currentName);
              setShowRenameModal(true);
              setContextMenu(null);
            }}
          >
            <Edit size={14} />
            Rename
          </button>
          <button
            className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-red-600 dark:text-red-400"
            onClick={() => {
              setModalPath(contextMenu.path);
              setShowDeleteModal(true);
              setContextMenu(null);
            }}
          >
            <Trash2 size={14} />
            Delete
          </button>
        </div>
      )}

      {/* Create File Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New File"
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="file-name">File Name</Label>
            <Input
              id="file-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="index.html"
              className="mt-1"
              autoFocus
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowCreateModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (newName.trim()) {
                  const filePath = modalPath ? `${modalPath}/${newName}` : newName;
                  onFileCreate(filePath);
                  setShowCreateModal(false);
                }
              }}
              disabled={!newName.trim()}
              className="flex-1"
            >
              Create
            </Button>
          </div>
        </div>
      </Modal>

      {/* Rename Modal */}
      <Modal
        isOpen={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        title="Rename"
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="rename-input">New Name</Label>
            <Input
              id="rename-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="mt-1"
              autoFocus
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowRenameModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (newName.trim()) {
                  const dir = modalPath.substring(0, modalPath.lastIndexOf('/'));
                  const newPath = dir ? `${dir}/${newName}` : newName;
                  onFileRename(modalPath, newPath);
                  setShowRenameModal(false);
                }
              }}
              disabled={!newName.trim()}
              className="flex-1"
            >
              Rename
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => !isDeleting && setShowDeleteModal(false)}
        title="Confirm Delete"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Are you sure you want to delete <strong>{modalPath}</strong>?
            This action cannot be undone.
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              disabled={isDeleting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                setIsDeleting(true);
                try {
                  await onFileDelete(modalPath);
                  setShowDeleteModal(false);
                } finally {
                  setIsDeleting(false);
                }
              }}
              disabled={isDeleting}
              className="flex-1"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};